import { ChangeDetectorRef, Component, Input, OnDestroy, OnInit } from '@angular/core';
import {
  Validators,
  UntypedFormBuilder,
  NG_VALUE_ACCESSOR,
  ControlValueAccessor,
  NG_VALIDATORS,
  Validator,
} from '@angular/forms';
import { InternationalAddress } from '@common/entities';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { StringValidator } from '../shared/validators/string.validator';
import { CountryList } from '@common/constants';
import { InternationalAddressLabels } from '@common/labels';

@Component({
  standalone: false,
  selector: 'common-international-address',
  templateUrl: './international-address.component.html',
  styleUrls: ['./international-address.component.scss'],
  providers: [
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    { provide: NG_VALUE_ACCESSOR, useExisting: InternationalAddressComponent, multi: true },
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    { provide: NG_VALIDATORS, useExisting: InternationalAddressComponent, multi: true },
  ],
})
export class InternationalAddressComponent implements OnInit, OnDestroy, ControlValueAccessor, Validator {
  labels = InternationalAddressLabels;

  get countries() {
    return [
      ...Object.values(CountryList).filter(
        (c) => !this.excludeCanada || (this.excludeCanada && c.displayName.toLowerCase() != 'canada')
      ),
    ];
  }

  addressLookupControl = this.formBuilder.control('');
  formGroup = this.formBuilder.group(
    {
      streetAddress: [''],
      suiteNumber: [''],
      city: [''],
      provState: [''],
      postalZip: [''],
      country: [''],
    },
    { updateOn: 'blur' }
  );
  destroy$ = new Subject<void>();

  @Input() showStreetAddressPlaceholder = false;
  @Input() dataCyPrefix = '';
  @Input() hideOptionalLabel = false;
  @Input() excludeCanada = true;
  @Input() excludeUnknown = true;
  /**
   * Is touched.  This is a bit of a hack, because CVA doesn't propogate markAllAsTouched
   */
  @Input() set isTouched(value) {
    value ? this.formGroup.markAllAsTouched() : this.formGroup.markAsUntouched();
  }

  _isRequired: boolean;
  @Input() set isRequired(value: boolean) {
    this._isRequired = value;
    if (value) {
      this.formGroup.controls.streetAddress.setValidators([Validators.required, StringValidator.notEmpty]);
      this.formGroup.controls.streetAddress.updateValueAndValidity();
      this.formGroup.controls.city.setValidators([Validators.required, StringValidator.notEmpty]);
      this.formGroup.controls.city.updateValueAndValidity();

      this.formGroup.controls.provState.setValidators([Validators.required, StringValidator.notEmpty]);
      this.formGroup.controls.provState.updateValueAndValidity();
      this.formGroup.controls.postalZip.setValidators([Validators.required]);
      this.formGroup.controls.postalZip.updateValueAndValidity();
      this.formGroup.controls.country.setValidators([Validators.required, StringValidator.notEmpty]);
      this.formGroup.controls.country.updateValueAndValidity();
    } else {
      this.formGroup.controls.streetAddress.clearValidators();
      this.formGroup.controls.streetAddress.updateValueAndValidity();
      this.formGroup.controls.city.clearValidators();
      this.formGroup.controls.city.updateValueAndValidity();
      this.formGroup.controls.provState.clearValidators();
      this.formGroup.controls.provState.updateValueAndValidity();
      this.formGroup.controls.postalZip.clearValidators();
      this.formGroup.controls.postalZip.updateValueAndValidity();
      this.formGroup.controls.country.clearValidators();
      this.formGroup.controls.country.updateValueAndValidity();
    }
  }
  get isRequired() {
    return this._isRequired;
  }

  constructor(private formBuilder: UntypedFormBuilder, private cdr: ChangeDetectorRef) {}

  async ngOnInit(): Promise<void> {
    this.formGroup.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value) => {
      if (value?.streetAddress) value.streetAddress = value.streetAddress.trim();
      if (value?.city) value.city = value.city.trim();
      if (value?.postalZip) value.postalZip = value.postalZip.trim();
      if (value?.suiteNumber) value.suiteNumber = value.suiteNumber.trim();
      if (value?.provState) value.provState = value.provState.trim();
      if (value?.country) value.country = value.country.trim();
      value.requiresValidation = this._isRequired;
      this.changed(value);
    });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  validate() {
    return this.formGroup.valid ? null : { invalid: true };
  }

  writeValue(address: InternationalAddress): void {
    if (!address)
      address = {
        streetAddress: '',
        suiteNumber: '',
        city: '',
        provState: '',
        postalZip: '',
        country: '',
        requiresValidation: this._isRequired,
      };

    address.streetAddress = address.streetAddress?.trim() ?? '';
    address.city = address.city?.trim() ?? '';
    address.postalZip = address.postalZip?.trim() ?? '';
    address.suiteNumber = address.suiteNumber?.trim() ?? '';

    this.formGroup.patchValue(address, { emitEvent: false });
  }

  private changed;
  registerOnChange(fn) {
    this.changed = fn;
  }
  private touched;
  registerOnTouched(fn) {
    this.touched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    isDisabled ? this.formGroup.disable({ emitEvent: false }) : this.formGroup.enable({ emitEvent: false });
  }
}
